import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { FormValidation } from "react-jsonschema-form";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useKeycloak } from "../../components/keycloak-provider";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import actions from "../../lib/actions";
import { IConfig, useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { CustomToast } from "../ariManagement/shared/toast";
import CrudForm from "../shared/crud/form";
import { CreatePropertyEntity, CreatePropertyOptions } from "./domain/createProperty.entity";
import { buildFormConfiguration } from "./domain/createProperty.formConfiguration";
import schema from "./domain/createProperty.schema.json";
import { executeCloned } from "./infrastructure/createProperty.api";
import { messages } from "./messages";

function CreateProperty() {
  const { replicatePropertyEndpoint }: IConfig = useConfig();
  const keycloak = useKeycloak();
  const createPropertyClient = useHttpClient(replicatePropertyEndpoint);
  const propertyTickers = useUserPropertyTickers();
  const intl = useIntl();

  const [toastType, setToastType] = useState<"success" | "error" | undefined>(undefined);

  const formConfiguration = useMemo(() => buildFormConfiguration(), []);

  const handleGet = useCallback(async () => {
    return Promise.resolve<CreatePropertyEntity>({
      id: "",
      originPropertyTicker: "template_engine.com",
      targetPropertyTicker: "",
      options: [],
    });
  }, []);

  const validate = useCallback(
    (formData: CreatePropertyEntity, errors: FormValidation) => {
      if (propertyTickers && propertyTickers.includes(formData?.targetPropertyTicker)) {
        errors.targetPropertyTicker.addError(intl.formatMessage(messages.alreadyExists));
      }
      return errors;
    },
    [intl, propertyTickers]
  );

  const handleCreate = useCallback(
    async ({ originPropertyTicker, targetPropertyTicker, options }: CreatePropertyEntity) => {
      const emails = keycloak.profile?.email ? [keycloak.profile?.email] : undefined;
      await executeCloned(
        createPropertyClient,
        originPropertyTicker,
        targetPropertyTicker,
        {
          migrateAri: options.includes(CreatePropertyOptions.MIGRATE_ARI),
        },
        emails
      );
      setToastType("success");
      return "success";
    },
    [createPropertyClient, keycloak.profile?.email]
  );

  return (
    <>
      <CrudForm<CreatePropertyEntity>
        id={"createProperty"}
        schema={schema}
        formConfiguration={formConfiguration}
        messages={messages}
        onCreate={handleCreate}
        onGet={handleGet}
        canCreate={actions.CreateProperty}
        showDirtyMessage={false}
        validate={validate}
      />
      <CustomToast
        show={!!toastType}
        type={toastType}
        successfulMessage={messages.successful}
        onClose={() => setToastType(undefined)}
      />
    </>
  );
}

export default withAuthorizationVerifier(CreateProperty);
